import { useEffect, useState } from "react";

import {
  Callout,
  FormGroup,
  Icon,
  InputGroup,
  Intent,
  Menu,
  MenuItem,
} from "@blueprintjs/core";
import EmojiPicker, { Emoji, EmojiStyle } from "emoji-picker-react";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router";

import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { useAuth } from "@/components/lib/auth";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { Button } from "@/components/ui/button";
import { SharingStatusIcon } from "@/components/ui/sharing/SharingStatusIcon";
import {
  SharingStatus,
  useCloneCollectionMutation,
  VisibilityScope,
} from "@/graphql";

const workspaceIcon = (
  <SharingStatusIcon
    size={14}
    sharingStatus={SharingStatus.Workspace}
    showTooltip={false}
  />
);
const workspaceText = "Workspace";
const onlyMeIcon = (
  <SharingStatusIcon
    size={14}
    sharingStatus={SharingStatus.Private}
    showTooltip={false}
  />
);
const onlyMeText = "Private";

interface CloneCollectionDialogProps {
  open: boolean;
  onClose: () => void;
  collection: { emojiUnicode?: string; name: string; id: string };
}

export function CloneCollectionDialog({
  open,
  onClose,
  collection,
}: CloneCollectionDialogProps) {
  const { currentWorkspace } = useAuth();
  const [visibilityScope, setVisibilityScope] = useState<VisibilityScope>(
    VisibilityScope.User,
  );
  const [openEmojiPicker, setOpenEmojiPicker] = useState(false);
  const [emoji, setEmoji] = useState(collection.emojiUnicode || "1f389");

  const [buttonIcon, buttonText] =
    visibilityScope === VisibilityScope.Workspace
      ? [<Icon icon={workspaceIcon} />, workspaceText]
      : [<Icon icon={onlyMeIcon} />, onlyMeText];

  const [name, setName] = useState(`${collection.name} (Copy)`);
  const [errorMessage, setErrorMessage] = useState<string | null>("123123");
  const navigate = useNavigate();

  useEffect(() => {
    setName(`${collection.name} (Copy)`);
    setEmoji(collection.emojiUnicode || "1f389");
  }, [collection]);

  const [clone, { loading }] = useCloneCollectionMutation({
    onCompleted: (data) => {
      if (data.cloneCollection?.ok) {
        const redirectUrl = `/roleplays/${data.cloneCollection?.collection?.slug}`;
        navigate(redirectUrl);
        onClose();
      }
      if (data.cloneCollection?.errors) {
        setErrorMessage("Couldn't clone collection");
      }
    },
    onError: () => {
      setErrorMessage("Couldn't clone collection");
    },
  });

  const onCloneCollection = async () => {
    setErrorMessage(null);
    clone({
      variables: {
        collectionId: collection.id,
        name,
        visibilityScope,
        emojiUnicode: emoji?.toUpperCase(),
      },
    });
  };

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title="Clone Collection"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setErrorMessage(null);
      }}
    >
      <div className="px-6 py-5">
        {errorMessage && (
          <div className="mb-2">
            <Callout icon={null} intent={Intent.DANGER}>
              {errorMessage}
            </Callout>
          </div>
        )}
        <div className="w-full">
          <div className="font-semibold text-base">Collection Name</div>
          <div className="flex gap-2 mt-1">
            <Popover
              onClose={() => setOpenEmojiPicker(false)}
              isOpen={openEmojiPicker}
              content={
                <EmojiPicker
                  previewConfig={{
                    showPreview: false,
                  }}
                  open={openEmojiPicker}
                  onEmojiClick={(emoji) => {
                    setEmoji(emoji.unified);
                    setOpenEmojiPicker(false);
                  }}
                  skinTonesDisabled
                  lazyLoadEmojis
                  categories={[]}
                />
              }
            >
              <Button
                size="icon"
                intent="secondary"
                variant="outline"
                onClick={() => setOpenEmojiPicker(true)}
                className="border-gray-300"
                style={{
                  height: 40,
                  width: 40,
                }}
              >
                <Emoji
                  unified={emoji.toLocaleLowerCase()}
                  emojiStyle={EmojiStyle.APPLE}
                  size={16}
                />
              </Button>
            </Popover>

            <InputGroup
              fill
              style={{
                borderRadius: 4,
                height: 40,
              }}
              onChange={(e) => {
                setName(e.target.value);
              }}
              value={name}
              placeholder="My Super Cool New Category"
            />
          </div>
          {!currentWorkspace.isPersonal && (
            <div className="mt-5">
              <div className="font-semibold text-base">Visibility Scope</div>
              <div className="mt-1">
                <Popover
                  content={
                    <Menu>
                      {visibilityScope === VisibilityScope.User ? (
                        <MenuItem
                          onClick={() =>
                            setVisibilityScope(VisibilityScope.Workspace)
                          }
                          text={workspaceText}
                          icon={workspaceIcon}
                        />
                      ) : (
                        <MenuItem
                          onClick={() =>
                            setVisibilityScope(VisibilityScope.User)
                          }
                          text={onlyMeText}
                          icon={onlyMeIcon}
                        />
                      )}
                    </Menu>
                  }
                  placement="bottom-end"
                >
                  <Button intent="secondary" size="sm" className="w-[150px]">
                    {buttonIcon}
                    {buttonText}
                    <ChevronDown />
                  </Button>
                </Popover>
              </div>
            </div>
          )}

          <FormGroup style={{ margin: 0, marginTop: 12 }}>
            <div className="flex mt-5 gap-2">
              <Button
                style={{ minWidth: 160 }}
                intent="primary"
                disabled={!name.trim() || loading}
                onClick={onCloneCollection}
              >
                Clone
              </Button>
              <Button onClick={onClose} intent="secondary" variant="minimal">
                Cancel
              </Button>
            </div>
          </FormGroup>
        </div>
      </div>
    </StyledDialog>
  );
}
